import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { MESES, formatCLP, formatFecha, hoyISO, parseFecha } from '../lib/format';
import { equivalenteMensual, gastoFijoPagado, mismoPeriodoGastoFijo } from '../lib/finanzas';
import { q, crearMovimiento, eliminarMovimiento } from '../lib/vinculos';
import { useAccion } from '../lib/useAccion';
import {
  PORCENTAJES_COMPARTIDO, personaCompartidaGuardada, parteCompartida, validarCompartido,
  registrarParteCompartida, personasConocidas,
} from '../lib/compartido';
import ErrorBanner from './ErrorBanner';
import { SelectRubro, CampoComprobante } from './ComprobanteCampos';
import { subirComprobante, nombreRubro } from '../lib/comprobantes';

const FRECUENCIAS = [
  { value: 'mensual', label: 'Mensual' },
  { value: 'semanal', label: 'Semanal' },
  { value: 'quincenal', label: 'Quincenal' },
  { value: 'semestral', label: 'Semestral' },
  { value: 'anual', label: 'Anual' },
];

const gastoVacio = {
  nombre: '', monto: '', categoria: '', frecuencia: 'mensual',
  rubro: '', dia_vencimiento: '', compartido: false, compartido_con: '', compartido_pct: 50,
};

// Campos extra que se guardan en la base (rubro, vencimiento y si se comparte).
const camposExtra = (f) => ({
  rubro: f.rubro || null,
  dia_vencimiento: f.dia_vencimiento ? Math.min(31, Math.max(1, parseInt(f.dia_vencimiento, 10))) : null,
  compartido_con: f.compartido && f.compartido_con.trim() ? f.compartido_con.trim() : null,
  compartido_pct: f.compartido && f.compartido_con.trim() ? Number(f.compartido_pct) : null,
});

function FixedExpensesManagement({ userId }) {
  const [gastos, setGastos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newExpense, setNewExpense] = useState(gastoVacio);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(gastoVacio);
  const [bolsillos, setBolsillos] = useState([]);
  const [pagosGF, setPagosGF] = useState([]); // movimientos que pagaron un gasto fijo (más reciente primero)
  const [personas, setPersonas] = useState([]);
  const [pago, setPago] = useState(null); // formulario "Pagar" abierto
  const { saving, error, setError, run } = useAccion();

  const hoy = new Date();
  const currentMonth = hoy.getMonth() + 1;

  const loadData = useCallback(async () => {
    if (!userId) return;
    const [data, b, pagos, pers] = await Promise.all([
      q(supabase.from('gastos_fijos').select('*').eq('user_id', userId).order('created_at', { ascending: true })),
      q(supabase.from('bolsillos').select('id, nombre').eq('user_id', userId).order('created_at')),
      q(supabase.from('bolsillo_movimientos').select('*').eq('user_id', userId)
        .not('gasto_fijo_id', 'is', null).order('fecha', { ascending: false })),
      personasConocidas(userId),
    ]);
    setGastos(data || []);
    setBolsillos(b || []);
    setPagosGF(pagos || []);
    setPersonas(pers);
  }, [userId]);

  useEffect(() => {
    loadData().catch(e => setError('Error cargando datos: ' + e.message)).finally(() => setLoading(false));
  }, [loadData, setError]);

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!newExpense.nombre || !newExpense.monto) return;
    const ok = await run(() => q(supabase.from('gastos_fijos').insert({
      user_id: userId,
      nombre: newExpense.nombre,
      monto: parseFloat(newExpense.monto),
      categoria: newExpense.categoria,
      frecuencia: newExpense.frecuencia,
      ...camposExtra(newExpense),
      pagado: false,
      mes_pago: null,
      ano_pago: null,
    })), loadData);
    if (ok) setNewExpense(gastoVacio);
  };

  const pagosDe = (g) => pagosGF.filter(m => m.gasto_fijo_id === g.id);
  const ultimoPago = (g) => pagosDe(g)[0] || null;

  // Movimiento con el que se pagó el período actual (para poder deshacerlo).
  const pagoDelPeriodo = (g) => pagosDe(g).find(m => {
    const f = parseFecha(m.fecha);
    return mismoPeriodoGastoFijo(g, f.getMonth() + 1, f.getFullYear(), new Date(g.ano_pago, (g.mes_pago || 1) - 1, 1));
  });

  const abrirPago = (g) => {
    const ultimo = ultimoPago(g);
    setPago({
      gasto: g,
      monto: g.monto,
      bolsillo_id: ultimo?.bolsillo_id || bolsillos[0]?.id || '',
      fecha: hoyISO(),
      compartido: !!g.compartido_con,
      compartidoCon: g.compartido_con || personaCompartidaGuardada(),
      compartidoPct: g.compartido_pct || 50,
      archivo: null,
    });
  };

  // Un solo paso: registra la salida de dinero, marca el gasto como pagado y deja
  // el monto pagado como estimado del próximo período (sirve para cuentas que varían).
  const handlePagar = async (e) => {
    e.preventDefault();
    const g = pago.gasto;
    const monto = parseFloat(pago.monto);
    if (!monto || monto <= 0) { setError('Ingresa cuánto pagaste.'); return; }
    if (pago.compartido && pago.bolsillo_id) {
      const err = validarCompartido({ persona: pago.compartidoCon, monto, pct: pago.compartidoPct });
      if (err) { setError(err); return; }
    }
    const f = parseFecha(pago.fecha);
    const descripcion = `Gasto fijo: ${g.nombre}`;
    const ok = await run(async () => {
      let mov = null;
      if (pago.bolsillo_id) {
        mov = await crearMovimiento({
          userId, bolsilloId: pago.bolsillo_id, tipo: 'egreso', monto, descripcion, fecha: pago.fecha, gastoFijoId: g.id, rubro: g.rubro || null,
        });
        if (pago.compartido) {
          await registrarParteCompartida({
            userId, persona: pago.compartidoCon, pct: pago.compartidoPct, monto, fecha: pago.fecha, descripcion: g.nombre,
          });
        }
      }
      await q(supabase.from('gastos_fijos').update({
        pagado: true,
        mes_pago: f.getMonth() + 1,
        ano_pago: f.getFullYear(),
        monto,
      }).eq('id', g.id));
      if (pago.archivo) {
        await subirComprobante({
          userId, file: pago.archivo, fecha: pago.fecha, monto, concepto: g.nombre,
          rubro: g.rubro || null, gastoFijoId: g.id, movimientoId: mov?.id || null,
        });
      }
    }, loadData);
    if (ok) setPago(null);
  };

  const handleMarkUnpaid = async (gasto) => {
    const mov = pagoDelPeriodo(gasto);
    if (mov) {
      if (!window.confirm(`¿Desmarcar "${gasto.nombre}"?\n\nTambién se borrará la salida de ${formatCLP(mov.monto)} del ${formatFecha(mov.fecha)}.`)) return;
      await run(() => eliminarMovimiento(mov), loadData);
      return;
    }
    await run(() => q(supabase.from('gastos_fijos').update({
      pagado: false,
      mes_pago: null,
      ano_pago: null,
    }).eq('id', gasto.id)), loadData);
  };

  const handleEdit = (gasto) => {
    setEditingId(gasto.id);
    setEditForm({
      nombre: gasto.nombre,
      monto: gasto.monto,
      categoria: gasto.categoria || '',
      frecuencia: gasto.frecuencia,
      rubro: gasto.rubro || '',
      dia_vencimiento: gasto.dia_vencimiento || '',
      compartido: !!gasto.compartido_con,
      compartido_con: gasto.compartido_con || '',
      compartido_pct: gasto.compartido_pct || 50,
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    const ok = await run(() => q(supabase.from('gastos_fijos').update({
      nombre: editForm.nombre,
      monto: parseFloat(editForm.monto),
      categoria: editForm.categoria,
      frecuencia: editForm.frecuencia,
      ...camposExtra(editForm),
    }).eq('id', editingId)), loadData);
    if (ok) setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este gasto fijo?')) return;
    await run(() => q(supabase.from('gastos_fijos').delete().eq('id', id)), loadData);
  };

  const isPaid = (g) => gastoFijoPagado(g, hoy);
  const pendientes = gastos.filter(g => !isPaid(g));
  const pagados = gastos.filter(g => isPaid(g));
  const sumaMensual = (lista) => lista.reduce((s, g) => s + equivalenteMensual(g), 0);

  if (loading) return (
    <div className="bolsillos-loading"><div className="loading-spinner"></div><p>Cargando...</p></div>
  );

  return (
    <div className="fixed-expenses-management">
      <div className="page-header">
        <div>
          <h2>Gastos Fijos</h2>
          <p className="page-subtitle">Tus compromisos recurrentes de {MESES[currentMonth - 1]}</p>
        </div>
      </div>

      <ErrorBanner error={error} onClose={() => setError('')} />

      {/* Resumen del mes (semanales, quincenales y anuales se llevan a su equivalente mensual) */}
      <div className="summary-cards" style={{ marginBottom: 8 }}>
        <div className="card">
          <div className="card-icon">🏠</div>
          <div className="card-content">
            <h4>Total mensual</h4>
            <p className="card-amount neutral">{formatCLP(sumaMensual(gastos))}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-icon">⏳</div>
          <div className="card-content">
            <h4>Por pagar</h4>
            <p className="card-amount negative">{formatCLP(sumaMensual(pendientes))}</p>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{pendientes.length} gastos</span>
          </div>
        </div>
        <div className="card">
          <div className="card-icon">✅</div>
          <div className="card-content">
            <h4>Ya pagado</h4>
            <p className="card-amount positive">{formatCLP(sumaMensual(pagados))}</p>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{pagados.length} gastos</span>
          </div>
        </div>
      </div>
      <p className="field-hint" style={{ marginBottom: 24 }}>
        Los gastos semanales, quincenales y anuales se muestran en su equivalente mensual.
      </p>

      {/* Formulario para agregar */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ marginBottom: 16, fontSize: '1rem', color: 'var(--text)' }}>Agregar gasto fijo</h3>
        <form onSubmit={handleAddExpense}>
          <div className="form-row" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Nombre (ej: Arriendo)"
              value={newExpense.nombre}
              onChange={e => setNewExpense({ ...newExpense, nombre: e.target.value })}
              required
              style={{ flex: '2 1 160px' }}
            />
            <input
              type="number"
              placeholder="Monto"
              value={newExpense.monto}
              onChange={e => setNewExpense({ ...newExpense, monto: e.target.value })}
              required
              min="0"
              style={{ flex: '1 1 110px' }}
            />
            <input
              type="text"
              placeholder="Categoría (opcional)"
              value={newExpense.categoria}
              onChange={e => setNewExpense({ ...newExpense, categoria: e.target.value })}
              style={{ flex: '1 1 130px' }}
            />
            <select
              value={newExpense.frecuencia}
              onChange={e => setNewExpense({ ...newExpense, frecuencia: e.target.value })}
              style={{ flex: '1 1 110px' }}
            >
              {FRECUENCIAS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <button type="submit" className="btn-primary" disabled={saving} style={{ flex: '0 0 auto' }}>
              {saving ? '...' : '+ Agregar'}
            </button>
          </div>
          <CamposExtra form={newExpense} setForm={setNewExpense} />
        </form>
      </div>

      {/* Lista de gastos */}
      {gastos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)', background: 'white', borderRadius: 12 }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🏠</div>
          <p>No tienes gastos fijos registrados.</p>
          <p style={{ fontSize: '0.9rem' }}>Agrega tus compromisos recurrentes: arriendo, servicios, suscripciones...</p>
        </div>
      ) : (
        <div className="recent-transactions">
          <h3>Lista de gastos</h3>
          <ul>
            {gastos.map(g => (
              <li key={g.id} className="transaction-item" style={{ alignItems: 'flex-start', flexDirection: 'column', gap: 8 }}>
                {editingId === g.id ? (
                  <form onSubmit={handleSaveEdit} style={{ width: '100%', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      value={editForm.nombre}
                      onChange={e => setEditForm({ ...editForm, nombre: e.target.value })}
                      required
                      style={{ flex: '2 1 140px' }}
                    />
                    <input
                      type="number"
                      value={editForm.monto}
                      onChange={e => setEditForm({ ...editForm, monto: e.target.value })}
                      required
                      style={{ flex: '1 1 100px' }}
                    />
                    <input
                      type="text"
                      value={editForm.categoria}
                      onChange={e => setEditForm({ ...editForm, categoria: e.target.value })}
                      placeholder="Categoría"
                      style={{ flex: '1 1 120px' }}
                    />
                    <select
                      value={editForm.frecuencia}
                      onChange={e => setEditForm({ ...editForm, frecuencia: e.target.value })}
                      style={{ flex: '1 1 110px' }}
                    >
                      {FRECUENCIAS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                    <div style={{ width: '100%' }}><CamposExtra form={editForm} setForm={setEditForm} /></div>
                    <button type="submit" className="btn-primary" disabled={saving}>Guardar</button>
                    <button type="button" className="btn-secondary" onClick={() => setEditingId(null)}>Cancelar</button>
                  </form>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, color: 'var(--text)' }}>{g.nombre}</span>
                        {g.categoria && (
                          <span style={{ fontSize: '0.75rem', background: 'var(--primary-xlight)', color: 'var(--primary)', padding: '2px 8px', borderRadius: 99 }}>
                            {g.categoria}
                          </span>
                        )}
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          {g.frecuencia}{g.dia_vencimiento ? ` · vence el ${g.dia_vencimiento}` : ''}
                          {g.rubro ? ` · ${nombreRubro(g.rubro)}` : ''}
                          {g.compartido_con ? ` · 👥 ${g.compartido_pct}% ${g.compartido_con}` : ''}
                        </span>
                        <span className={isPaid(g) ? 'text-success' : 'text-danger'} style={{ fontSize: '0.8rem', fontWeight: 500 }}>
                          {isPaid(g) ? (g.frecuencia === 'anual' ? '✅ Pagado este año' : g.frecuencia === 'semestral' ? '✅ Pagado este semestre' : '✅ Pagado') : '⏳ Pendiente'}
                        </span>
                      </div>
                    </div>
                    <span style={{ textAlign: 'right' }}>
                      <span className={`transaction-amount ${isPaid(g) ? 'positive' : 'negative'}`}>
                        {isPaid(g) ? '' : '≈ '}{formatCLP(g.monto)}
                      </span>
                      {!isPaid(g) && (
                        <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          {ultimoPago(g) ? 'según último pago' : 'estimado'}
                        </span>
                      )}
                      {g.frecuencia !== 'mensual' && (
                        <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          ≈ {formatCLP(equivalenteMensual(g))}/mes
                        </span>
                      )}
                    </span>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      {isPaid(g) ? (
                        <button
                          className="btn-secondary"
                          style={{ padding: '4px 10px', fontSize: '0.78rem' }}
                          onClick={() => handleMarkUnpaid(g)}
                          disabled={saving}
                          title="Marcar como pendiente"
                        >
                          Desmarcar
                        </button>
                      ) : (
                        <button
                          className="btn-primary"
                          style={{ padding: '4px 10px', fontSize: '0.78rem' }}
                          onClick={() => abrirPago(g)}
                          disabled={saving}
                        >
                          💸 Pagar
                        </button>
                      )}
                      <button
                        className="btn-secondary"
                        style={{ padding: '4px 10px', fontSize: '0.78rem' }}
                        onClick={() => handleEdit(g)}
                      >
                        ✎
                      </button>
                      <button
                        className="btn-secondary"
                        style={{ padding: '4px 10px', fontSize: '0.78rem', color: 'var(--danger)' }}
                        onClick={() => handleDelete(g.id)}
                        disabled={saving}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Modal: pagar gasto fijo */}
      {pago && (
        <div className="modal-overlay" onClick={() => setPago(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Pagar {pago.gasto.nombre}</h3>
              <button className="modal-close" onClick={() => setPago(null)}>×</button>
            </div>
            <form onSubmit={handlePagar}>
              <div className="form-group">
                <label>¿Cuánto salió? ($)</label>
                <input type="number" min="1" inputMode="numeric" value={pago.monto}
                  onChange={e => setPago({ ...pago, monto: e.target.value })} required autoFocus
                  onFocus={e => e.target.select()} />
                {ultimoPago(pago.gasto) ? (
                  <span className="field-hint">
                    Último pago: {formatCLP(ultimoPago(pago.gasto).monto)} el {formatFecha(ultimoPago(pago.gasto).fecha)}
                  </span>
                ) : (
                  <span className="field-hint">Escribe el monto real de la boleta; queda como estimado para el próximo período.</span>
                )}
              </div>
              <div className="form-group">
                <label>¿De qué categoría sale?</label>
                <select value={pago.bolsillo_id} onChange={e => setPago({ ...pago, bolsillo_id: e.target.value })}>
                  <option value="">Solo marcar como pagado (sin registrar salida)</option>
                  {bolsillos.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Fecha de pago</label>
                <input type="date" value={pago.fecha} onChange={e => setPago({ ...pago, fecha: e.target.value })} />
              </div>
              <CampoComprobante archivo={pago.archivo} onChange={f => setPago({ ...pago, archivo: f })} />
              {pago.bolsillo_id && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={pago.compartido}
                      onChange={e => setPago({ ...pago, compartido: e.target.checked })}
                      style={{ width: 18, height: 18, accentColor: 'var(--primary)' }} />
                    👥 Gasto compartido (alguien me debe una parte)
                  </label>
                  {pago.compartido && (
                    <>
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <input type="text" list="personas-compartido-gf" style={{ flex: 2 }}
                          value={pago.compartidoCon} placeholder="¿Con quién? ej: Esposa"
                          onChange={e => setPago({ ...pago, compartidoCon: e.target.value })} />
                        <datalist id="personas-compartido-gf">
                          {personas.map(p => <option key={p} value={p} />)}
                        </datalist>
                        <select style={{ flex: 1 }} value={pago.compartidoPct}
                          onChange={e => setPago({ ...pago, compartidoPct: Number(e.target.value) })}>
                          {PORCENTAJES_COMPARTIDO.map(p => <option key={p} value={p}>{p}%</option>)}
                        </select>
                      </div>
                      <span className="field-hint">
                        ✅ {pago.compartidoCon.trim() || 'Esa persona'} te queda debiendo {formatCLP(parteCompartida(pago.monto, pago.compartidoPct))} en Me Deben.
                      </span>
                    </>
                  )}
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setPago(null)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Guardando...' : 'Pagar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Rubro, día de vencimiento y si el gasto se comparte (ej. 50% con la pareja).
function CamposExtra({ form, setForm }) {
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
      <div style={{ flex: '1 1 150px' }}>
        <SelectRubro value={form.rubro} onChange={v => setForm({ ...form, rubro: v })} vacio="Rubro..." />
      </div>
      <input type="number" min="1" max="31" inputMode="numeric" placeholder="Vence el día (1-31)"
        value={form.dia_vencimiento} onChange={e => setForm({ ...form, dia_vencimiento: e.target.value })}
        style={{ flex: '1 1 140px' }} />
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', flex: '0 0 auto', margin: 0 }}>
        <input type="checkbox" checked={form.compartido}
          onChange={e => setForm({ ...form, compartido: e.target.checked, compartido_con: form.compartido_con || personaCompartidaGuardada() })}
          style={{ width: 18, height: 18, accentColor: 'var(--primary)' }} />
        👥 Compartido
      </label>
      {form.compartido && (
        <>
          <input type="text" placeholder="¿Con quién?" value={form.compartido_con}
            onChange={e => setForm({ ...form, compartido_con: e.target.value })} style={{ flex: '1 1 120px' }} />
          <select value={form.compartido_pct} onChange={e => setForm({ ...form, compartido_pct: Number(e.target.value) })}
            style={{ flex: '0 1 90px' }}>
            {PORCENTAJES_COMPARTIDO.map(p => <option key={p} value={p}>{p}%</option>)}
          </select>
        </>
      )}
    </div>
  );
}

export default FixedExpensesManagement;
